import { useQuery } from '@tanstack/react-query';

import type { TargetingClient } from './api';
import { targetingSectionIsVisible, TargetingFormSection } from './config';

const targetingLookupQueryOptions = {
  staleTime: 10 * 60 * 1000,
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const;

export function useTargetingFormOptions({
  client,
  sections,
}: {
  client: TargetingClient;
  sections: readonly TargetingFormSection[];
}) {
  const deviceTypesQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'device-types'],
    queryFn: client.listDeviceTypes,
    enabled: targetingSectionIsVisible(sections, TargetingFormSection.DeviceTypes),
    ...targetingLookupQueryOptions,
  });
  const iabCategoriesQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'iab-categories'],
    queryFn: () => client.listIabCategories({ page: 0, size: 1000 }),
    enabled: targetingSectionIsVisible(sections, TargetingFormSection.Content),
    ...targetingLookupQueryOptions,
  });
  const platformsQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'platforms', client.platformCatalog],
    queryFn: client.listPlatforms,
    enabled: targetingSectionIsVisible(sections, TargetingFormSection.Platforms),
    ...targetingLookupQueryOptions,
  });
  const languagesQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'languages'],
    queryFn: client.listLanguages,
    enabled: targetingSectionIsVisible(sections, TargetingFormSection.Languages),
    ...targetingLookupQueryOptions,
  });
  const supplyCompaniesQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'supply-companies'],
    queryFn: client.listSupplyCompanies,
    enabled: targetingSectionIsVisible(sections, TargetingFormSection.SupplyCompanies),
    ...targetingLookupQueryOptions,
  });
  const queries = [
    deviceTypesQuery,
    platformsQuery,
    iabCategoriesQuery,
    languagesQuery,
    supplyCompaniesQuery,
  ];

  return {
    deviceTypes: deviceTypesQuery.data ?? [],
    iabCategories: iabCategoriesQuery.data ?? [],
    languages: languagesQuery.data ?? [],
    platforms: platformsQuery.data ?? [],
    supplyCompanies: supplyCompaniesQuery.data ?? [],
    error: queries.find((query) => query.isError)?.error,
    loading: queries.some((query) => query.isLoading),
  };
}
